import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isViewer } from "@/lib/guards";
import AppHeader from "@/components/AppHeader";
import AvatarCardStrategy from "@/components/avatar/AvatarCardStrategy";
import WriteCopySheet from "@/components/avatar/WriteCopySheet";
import { CardGridSkeleton, ErrorState, EmptyState } from "@/components/shared/StateViews";
import DraftResumeSheet from "@/components/shared/DraftResumeSheet";
import { startAvatarCreation, discardDraft } from "@/lib/avatarFlow";
import { friendlyError } from "@/lib/errors";
import { Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AvatarLibrary() {
  const nav = useNavigate();
  const { user } = useAuth();
  const viewer = isViewer(user);
  const [avatars, setAvatars] = useState([]); // complete
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draftSheetId, setDraftSheetId] = useState(null);
  const [writeCopyFor, setWriteCopyFor] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, d] = await Promise.all([
        api.get("/avatars", { params: { status: "complete" } }),
        api.get("/avatars", { params: { status: "draft" } }),
      ]);
      setAvatars(c.data || []);
      setDrafts(d.data || []);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Favoritos primeiro, depois completos; drafts sempre no fim.
  const ordered = useMemo(() => {
    const favs = avatars.filter((a) => a.favorito);
    const rest = avatars.filter((a) => !a.favorito);
    return { favs, rest };
  }, [avatars]);

  const visibleAvatars = [...ordered.favs, ...ordered.rest, ...(!viewer ? drafts : [])];

  const toggleFavorite = async (avatar, next) => {
    // Optimistic in the list (card also flips locally); rethrow so the card rolls back.
    setAvatars((list) => list.map((a) => (a.id === avatar.id ? { ...a, favorito: next } : a)));
    try {
      await api.patch(`/avatars/${avatar.id}`, { favorito: next });
    } catch (err) {
      setAvatars((list) => list.map((a) => (a.id === avatar.id ? { ...a, favorito: !next } : a)));
      toast.error(friendlyError(err));
      throw err;
    }
  };

  const generatePhoto = async (avatar) => {
    setGeneratingId(avatar.id);
    try {
      const { data } = await api.post(`/avatars/${avatar.id}/generate-photo`);
      setAvatars((list) => list.map((a) => (a.id === avatar.id ? { ...a, foto_url: data.foto_url } : a)));
      toast.success("Retrato gerado ✦");
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setGeneratingId(null);
    }
  };

  const createAvatar = async () => {
    try {
      await startAvatarCreation({ nav, onDraftExists: (id) => setDraftSheetId(id) });
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  const hasAny = ordered.favs.length + ordered.rest.length + (viewer ? 0 : drafts.length) > 0;

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="avatar-library-page">
      <AppHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10 pb-24 md:pb-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 lg:mb-10">
          <div>
            <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-ui mb-2">
              Biblioteca
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-brand-ink">Meus avatares</h1>
            <p className="text-brand-ink2 text-[14.5px] mt-1 max-w-2xl">
              Crie múltiplas clientes ideais e escolha com quem falar num relance.
              {!loading && !error && (
                <span className="text-brand-sand"> {avatars.length} avatar{avatars.length === 1 ? "" : "es"} completo{avatars.length === 1 ? "" : "s"}.</span>
              )}
            </p>
          </div>
          {!viewer && (
            <button
              data-testid="new-avatar-btn"
              onClick={createAvatar}
              className="inline-flex items-center justify-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-5 sm:px-6 py-3 rounded-full font-ui text-[13px] transition-colors self-start sm:self-auto min-h-[44px]"
            >
              <Plus className="w-4 h-4" />
              Novo avatar
            </button>
          )}
        </div>

        {loading && <CardGridSkeleton count={6} />}
        {!loading && error && <ErrorState message={error} onRetry={load} testid="avatars-error" />}

        {!loading && !error && !hasAny && (
          <EmptyState
            icon={Sparkles}
            title={viewer ? "Nenhum avatar nesta conta" : "Crie sua primeira cliente ideal"}
            description={viewer
              ? "Esta marca ainda não tem avatares completos para visualizar."
              : "Uma entrevista de 7 blocos e a ValiosIA monta um avatar profundo, com a linguagem REAL da sua cliente."}
            actionLabel={viewer ? undefined : "Criar avatar"}
            onAction={viewer ? undefined : createAvatar}
            testid="empty-state"
          />
        )}

        {!loading && !error && hasAny && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5"
            data-testid="avatars-grid"
          >
            {[...ordered.favs, ...ordered.rest].map((a) => (
              <AvatarCardStrategy
                key={a.id}
                avatar={a}
                peerAvatars={visibleAvatars}
                onFavorite={viewer ? undefined : toggleFavorite}
                onWriteCopy={viewer ? undefined : setWriteCopyFor}
                onGeneratePhoto={viewer ? undefined : generatePhoto}
                onRegeneratePhoto={viewer ? undefined : generatePhoto}
                generating={generatingId === a.id}
                readOnly={viewer}
              />
            ))}
            {!viewer && drafts.map((d) => (
              <AvatarCardStrategy
                key={d.id}
                avatar={d}
                peerAvatars={visibleAvatars}
                selectable
                onSelect={() => nav(`/avatars/${d.id}/builder`)}
              />
            ))}
          </div>
        )}
      </div>

      <WriteCopySheet
        avatar={writeCopyFor}
        peerAvatars={visibleAvatars}
        open={Boolean(writeCopyFor)}
        onOpenChange={(o) => !o && setWriteCopyFor(null)}
      />

      <DraftResumeSheet
        draftId={draftSheetId}
        open={Boolean(draftSheetId)}
        onOpenChange={(o) => !o && setDraftSheetId(null)}
        onContinue={() => nav(`/avatars/${draftSheetId}/builder?mode=voice`)}
        onDiscard={async () => {
          try {
            await discardDraft();
            setDraftSheetId(null);
            await startAvatarCreation({ nav });
          } catch (err) {
            toast.error(friendlyError(err));
          }
        }}
      />
    </div>
  );
}
